import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { sendUserIdToAnalytics } from '@app/web-analytic';

import {
  AUTH,
  AnalyticEvent,
  MAIN,
  getMismatches,
  sendRequestErrorsAnalytics,
  useStores,
} from '@shared/index';
import type { LoginRequestParams } from '@shared/types';

import { useLogin } from './use-login';
import {
  injectAutoLogoutActions,
  setUserSessionAutoLogout,
} from './user-auto-logout';
import { UserContext } from './user-context';
import {
  LocalStorageUserInfoKeys,
  getCurrentUserInfo,
  isLoggedIn,
  putAuthenticationInfoInLocalStorage,
  removeUserInfoFromLocalStorage,
} from './user-context-utils';

const LOGOUT_QUERY_KEY = 'logout';

interface Props {
  children: JSX.Element;
}

export const UserAuthProvider = ({ children }: Props) => {
  const [username, setUsername] = useState<string | undefined | null>();
  const [password, setPassword] = useState<string | undefined | null>();
  const isAuthenticated = isLoggedIn();
  const queryClient = useQueryClient();
  const { MainStore } = useStores();
  const [redirectTo, setRedirectTo] = useState<string>();
  const navigate = useNavigate();

  const getUserName = () => {
    return username || localStorage.getItem(LocalStorageUserInfoKeys.name);
  };

  const { res, refetch, error, isFetching } = useLogin({
    username,
    password,
  });

  const handleLogout = useCallback(() => {
    removeUserInfoFromLocalStorage();
    setUsername(null);
    setPassword(null);
    MainStore.resetStore();
    navigate(AUTH, { replace: true, state: {} });
    queryClient.removeQueries({
      predicate: (query) => !query.queryKey.includes(LOGOUT_QUERY_KEY),
    });
  }, []);

  const handleLogin = useCallback((params: LoginRequestParams) => {
    setUsername(params.username);
    setPassword(params.password);
    sendUserIdToAnalytics(params.username || '');
    setRedirectTo(params.redirectTo);
  }, []);

  useEffect(() => {
    if (!!username && !!password) {
      refetch();
    }
  }, [username, password]);

  useEffect(() => {
    if (!isFetching && res) {
      navigate(redirectTo ? redirectTo : MAIN);
      putAuthenticationInfoInLocalStorage(res);
      setUserSessionAutoLogout();
      const { name } = getCurrentUserInfo();
      setUsername(name);
      setRedirectTo(undefined);
    }
  }, [isFetching, res]);

  useEffect(() => {
    if (error) {
      sendRequestErrorsAnalytics(
        AnalyticEvent.AGENT_AUTH_SIGNUP_ERROR,
        getMismatches(error)
      );
      sendRequestErrorsAnalytics(
        AnalyticEvent.AGENT_LOGIN_ERROR,
        getMismatches(error)
      );
    }
  }, [error]);

  useEffect(() => {
    injectAutoLogoutActions(handleLogout);
  }, [handleLogout]);

  const context = useMemo(
    () => ({
      handleLogin,
      handleLogout,
      isAuthenticated,
      loginError: error,
      username,
      getUserName,
    }),
    [handleLogin, handleLogout, isAuthenticated, error, username, getUserName]
  );

  return (
    <UserContext.Provider value={context}>{children}</UserContext.Provider>
  );
};
